import React, { PureComponent } from 'react'
import {
  node, string, bool, func, number,
} from 'prop-types'
import IconButton from '@active/react-ui/IconButton'
import noop from 'lodash/noop'
import KEY_CODES from '../../shared/utils/keyCodes'
import paymentMethodTypes, { paymentMethodPropType } from '../payments/components/common/paymentMethodTypes'

export class AccordionPanel extends PureComponent {
  static propTypes = {
    className: string,
    children: node.isRequired,
    onInitChildren: func,
    setStatus: func,
    index: number,
    transition: string,
    active: bool,
    title: node.isRequired,
    openIcon: string,
    closeIcon: string,
    completeIcon: string,
    disabled: bool,
    complete: bool,
    onChangeFocus: func,
    paymentMethod: paymentMethodPropType.isRequired,
  }

  _height

  _container

  _focusNode

  _isTransitionEnd

  _content

  static defaultProps = {
    openIcon: 'icon-plus',
    closeIcon: 'icon-minus',
    completeIcon: 'icon-check',
    onInitChildren: noop,
    setStatus: noop,
    onChangeFocus: noop,
    className: undefined,
    index: NaN,
    transition: undefined,
    active: false,
    disabled: false,
    complete: false,
  }

  componentDidMount() {
    const {
      transition, onInitChildren, index, disabled,
    } = this.props
    if (this._content && transition) {
      this._height = this._content.offsetHeight
    }
    this._isTransitionEnd = true
    onInitChildren(
      index,
      disabled ? undefined : this._focusNode,
    )
  }

  setAccordionBodyContainer = container => {
    this._container = container
  }

  setWrappedComponentInstance = content => {
    this._content = content
  }

  setWrappedFocusInstance = focusNode => {
    this._focusNode = focusNode
  }

  startTransition = () => {
    const { active } = this.props
    this.handleTransitionEnd()
    if (this._isTransitionEnd) {
      this._isTransitionEnd = false
      if (active && this._container) {
        this._height = this._container.offsetHeight
        this._container.style.height = `${this._height}px`
      }
      return true
    }
    return false
  }

  endTransition = () => {
    this._isTransitionEnd = true
  }

  validateTransition = action => {
    const { transition } = this.props
    if (transition.trim()) {
      if (this.startTransition()) {
        window.requestAnimationFrame(() => {
          action()
        })
      }
    } else {
      action()
    }
  }

  handleClick = index => e => {
    const { disabled, setStatus, active } = this.props
    e.preventDefault()
    if (!disabled) {
      this.validateTransition(() => {
        setStatus(index, !active)
      })
    }
  }

  handleKeyDown = e => {
    const {
      index, active, disabled, onChangeFocus,
    } = this.props
    const {
      ENTER, SPACE, UPARROW, DOWNARROW,
    } = KEY_CODES
    const operationKeyCodes = [ENTER, SPACE, UPARROW, DOWNARROW]

    e.persist()
    if (operationKeyCodes.indexOf(e.keyCode) !== -1) {
      e.preventDefault()
    }
    const changeFocus = () => {
      onChangeFocus(
        index,
        !active,
        e.keyCode,
        this._focusNode,
        disabled,
      )
    }
    if (e.keyCode === ENTER || e.keyCode === SPACE) {
      this.validateTransition(changeFocus)
    } else {
      changeFocus()
    }
  }

  handleTransitionEnd = () => {
    const { active } = this.props
    if (active) {
      if (this._container && this._container.style) {
        this._container.style.height = 'auto'
      }
    }
    this.endTransition()
  }

  render() {
    const {
      active, children, index, complete, className, title, disabled,
      openIcon, closeIcon, completeIcon, paymentMethod, transition: transitionFromProps,
    } = this.props
    const info = paymentMethodTypes.convert(paymentMethod)

    const items = []
    React.Children.map(children, (child, key) => {
      const item = React.cloneElement(child, {
        ...children.props,
        key,
        active,
      })

      items.push(item)
    })

    const transition = transitionFromProps.trim()

    let iconClassName
    if (complete) {
      iconClassName = completeIcon
    } else {
      iconClassName = active ? closeIcon : openIcon
    }

    let containerHeight
    if (active) {
      containerHeight = transition ? this._height : 'auto'
    } else {
      containerHeight = 0
    }
    const clickHandler = this.handleClick(index)
    return (
      <div
        className={`accordion ${active ? 'accordion--show' : ''} ${disabled
          ? 'accordion--disabled'
          : ''} ${complete ? 'accordion--complete' : ''} ${className || ''}`}
      >
        <div
          ref={this.setWrappedFocusInstance}
          onKeyDown={this.handleKeyDown}
          role="button"
          aria-expanded={!!active}
          tabIndex={disabled ? -1 : 0}
          aria-label={`${active ? 'Collapse' : 'Expand'} information for the card ending in ${info.getLastDigits()}`}
          className="accordion__header"
        >
          <div className="accordion__header-text">{title}</div>
          <div className="accordion__header-icon">
            <IconButton
              tabIndex={-1}
              className="accordion__header-button"
              alt="SpreadOrShrink"
              icon={iconClassName}
              onClick={clickHandler}
              onKeyPress={clickHandler}
            />
          </div>
        </div>
        <div
          className="accordion__body-container"
          style={{
            transition: `${transition || 'none'}`,
            overflow: 'hidden',
            height: containerHeight,
          }}
          ref={this.setAccordionBodyContainer}
          onTransitionEnd={this.handleTransitionEnd}
        >
          <div
            className="accordion__body"
            ref={this.setWrappedComponentInstance}
          >
            {items}
          </div>
        </div>
      </div>
    )
  }
}

export default AccordionPanel
